import React from 'react';
import ReactDOM from 'react-dom';
import { scroller } from 'react-scroll';
import bemBlock from '@gik/core/utils/bemBlock';
import { scrollIntoView } from '@gik/core/utils/scroll';
import { useTranslation } from 'react-i18next';
import { translationKeys } from '@gik/inkind-page/i18n/en';
import { Button, Form, FormSchemaEntry, SvgIcon, UI } from '@gik/ui';
import { useInkind } from '@gik/api/inkinds/inkind';
import { InkindPageAPIModel, PatchInkindPageRequest } from '@gik/core/models/gik/InkindPage';
import { patchInkind } from '@gik/api/inkinds/inkinds';
import withSuspense from '@gik/core/components/suspended/withSuspense';
import {
  FeaturesSection,
  FeaturesSectionValues,
  modalContentId,
  OrganizersSection,
  pageSettingsNavItemIds,
  PageSettingsNavItemIdsType,
  pageTitleFieldSetSchema,
  PageTitleSection,
  PageTitleValues,
  PrivacySettingsSection,
  PrivacySettingsValues,
  recipientInfoFieldSetSchema,
  RecipientInfoSection,
  RecipientInfoValues,
} from '@gik/inkind-page';
import { openIntercomWindow } from '@gik/core/utils/Intercom';
import { isBrowser, navigateTo } from '@/utils/environment';
import routes from '@gik/core/routes';
import { useUserStore } from '@gik/core/store/UserStore';
import { removeOrUnfollowPage } from '@gik/api/users/user';
// important, import Skeleton component like this and not from @gik/inkind-page
// or it will be undefined and fail silently, circular dependency issue
import InkindPageSettingsSkeleton from './InkindPageSettingsSkeleton';
import { extractCampaignSlug, getGoFundMeURL } from '@gik/core/utils/GoFundMe';
import { usePageGoFundMeCampaignValid } from '@gik/inkind-page/api/GoFundMeAPI';
import { GoFundMeFormInputNames } from '@gik/create/enums/GoFundMeFormInputNames';
import { PayPalFormInputNames } from '@gik/create/enums/PayPalFormInputNames';
import { featuresSectionSuggestedItems } from './FeaturesSection/FeaturesSection';
import InkindPageUpgradeSection from './InkindPageUpgradeSection/InkindPageUpgradeSection';
import he from 'he';

export const inkindPageSettingsFormBlockName = 'inkind-page-settings-form';

type PageSettingsFormValues = PageTitleValues & RecipientInfoValues;
type PageSettingsValues = PageSettingsFormValues & PrivacySettingsValues & FeaturesSectionValues;
export type PageSettingsScrollToType = PageSettingsNavItemIdsType | typeof featuresSectionSuggestedItems;

export interface IInkindPageSettingsFormProps {
  onClose?: () => void;
  inkindRouteId: string;
  buttonsPortal?: () => HTMLElement;
  scrollToSectionId?: PageSettingsScrollToType;
}

export const buttonsNonModalPortalId = 'buttons-non-modal-portal-id';
export const buttonsNonModalPortal = () => document.getElementById(buttonsNonModalPortalId);

const InkindPageSettingsForm: React.FC<IInkindPageSettingsFormProps> = ({
  onClose,
  inkindRouteId,
  buttonsPortal,
  scrollToSectionId,
}): React.ReactElement => {
  const formId = 'inkind-page-settings-form-id';

  const bem = bemBlock(inkindPageSettingsFormBlockName);
  const { t } = useTranslation();
  const userId = useUserStore(state => state.id);

  // NOTE: fix highlighting first item in scroll navbar
  // since first 4 section ids are wrapped with a form tag and Organizers section isn't
  // Organizers nav item gets incorrectly highlighted
  React.useEffect(() => {
    // if in modal
    if (isBrowser() && buttonsPortal) {
      // wont work without minimal delay
      setTimeout(() => {
        const contentEl = document.getElementById(modalContentId);
        contentEl?.dispatchEvent(new CustomEvent('scroll'));
      }, 1);
    }
  }, [buttonsPortal]);

  // states, sections with handlers
  const [featuresSectionValues, setFeaturesSectionValues] = React.useState<FeaturesSectionValues>(null);
  const [privacySettingsValues, setPrivacySettingsValues] = React.useState<PrivacySettingsValues>(null);

  const { revalidate: revalidateGoFundMeCampaignValidity } = usePageGoFundMeCampaignValid(inkindRouteId);

  // it takes some time to render the form
  // block rendering Organizers section before the form
  const [isFormReady, setIsFormReady] = React.useState<boolean>(false);

  const [deletePageLoading, setDeletePageLoading] = React.useState<boolean>(false);

  const { data: inkindPage, error: inkindPageError, revalidate: revalidateInkindPage } = useInkind(inkindRouteId);

  // scroll to section when modal opens
  React.useEffect(() => {
    // NOTE: must wait for Form to load and then scroll
    if (!isBrowser() || !buttonsPortal || !scrollToSectionId || !isFormReady) {
      return;
    }

    const scrollOptions = {
      duration: 300,
      smooth: true,
      containerId: modalContentId,
    };

    // small delay is needed for Features section to expand
    setTimeout(() => {
      scroller.scrollTo(scrollToSectionId, scrollOptions);
    }, 1200);
  }, [scrollToSectionId, buttonsPortal, isFormReady]);

  React.useEffect(() => {
    if (!inkindPage) {
      return;
    }

    const initialFeaturesSectionValues = getInitialFeaturesSectionValues(inkindPage);
    const initialPrivacySettingsValues = getInitialPrivacySettingsValues(inkindPage);
    setFeaturesSectionValues(initialFeaturesSectionValues);
    setPrivacySettingsValues(initialPrivacySettingsValues);
  }, [inkindPage]);

  const getInitialPrivacySettingsValues = (inkindPage: InkindPageAPIModel): PrivacySettingsValues => {
    return {
      // can be enabled and still have number and name defined
      phoneCallsEnabled: inkindPage.phoneCalls,
      alternateNumber: inkindPage.phoneCallRequest?.phone || '', // added to model
      alternateNumberOwnerName: inkindPage.phoneCallRequest?.name || '',
      flowersEnabled: inkindPage.flowers,
      textMessagesEnabled: inkindPage.phoneCallRequest?.IsTextsOk || false,
      visitorsEnabled: inkindPage.visitors,
      showPageEnabled: inkindPage.isPublic,
      // named same in GET and PATCH
      pageSharingEnabled: inkindPage.allowPageSharing,
      featurePageEnabled: inkindPage.canFeature,
      searchEnginesEnabled: inkindPage.allowCrawlers,
    };
  };

  const getInitialFeaturesSectionValues = (inkindPage: InkindPageAPIModel): FeaturesSectionValues => {
    return {
      careCalendarEnabled: inkindPage.isCalendar,
      wishlistValues: {
        enabled: inkindPage.wishList,
        // can be disabled and still have url defined
        amazonWishlistEnabled: inkindPage.amazonWishlist,
        amazonValue: inkindPage.amazonWishlistRequest?.amazonWishlistUrl || '', // null from backend
        isAmazonValueValid: true, // server sends valid data
      },
      donationValues: {
        enabled: inkindPage.payPal, // will be renamed
        [GoFundMeFormInputNames.PageURL]: getGoFundMeURL(inkindPage),
        [PayPalFormInputNames.EmailAddress]: inkindPage.financialContributions?.paypalEmail || '',
        isPayPalValueValid: true, // server sends valid data
        isGoFundMeValueValid: true,
      },
      sendGiftBoxEnabled: inkindPage.CTA, // will be renamed
      enableSuggestedArticles: inkindPage.enableSuggestedArticles,
      enableSuggestedProducts: inkindPage.enableSuggestedProducts,
    };
  };

  const getFeaturesSectionValid = () =>
    featuresSectionValues.wishlistValues.isAmazonValueValid &&
    featuresSectionValues.donationValues.isPayPalValueValid &&
    featuresSectionValues.donationValues.isGoFundMeValueValid;

  const getInitialFormValues = (inkindPage: InkindPageAPIModel): PageSettingsFormValues => {
    if (!inkindPage) {
      return null;
    }

    return {
      // Page Title section
      pageTitle: he.decode(inkindPage.title),
      // Recipient Info section
      recipientFullName: inkindPage.recipientFullName,
      address1: inkindPage.address,
      address2: inkindPage.address2,
      city: inkindPage.city,
      state: inkindPage.stateCode,
      postalCode: inkindPage.zip,
      recipientEmail: inkindPage.recipientEmail,
      confirmRecipientEmail: inkindPage.recipientEmail,
    };
  };

  // handle form bellow
  const schema: FormSchemaEntry[] = [...pageTitleFieldSetSchema(), ...recipientInfoFieldSetSchema()];

  const handleClick = () => {
    document.getElementById(formId).dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  };

  const handleSubmit = async (formData: PageSettingsFormValues) => {
    // merge with values from handlers
    const pageSettingsValues: PageSettingsValues = { ...formData, ...featuresSectionValues, ...privacySettingsValues };

    const patchInkindPageData: PatchInkindPageRequest = {
      enabledFeatures: {
        calendar: pageSettingsValues.careCalendarEnabled,
        wishlist: pageSettingsValues.wishlistValues.enabled,
        amazonWishlist: pageSettingsValues.wishlistValues.amazonWishlistEnabled,
        donations: pageSettingsValues.donationValues.enabled,
        giftboxes: pageSettingsValues.sendGiftBoxEnabled,
      },
      amazonWishlistUrl: pageSettingsValues.wishlistValues.amazonValue,
      paypalEmail: pageSettingsValues.donationValues.payPalValue,
      goFundMeUrl: extractCampaignSlug(pageSettingsValues.donationValues.gofundmeValue),
      pageTitle: pageSettingsValues.pageTitle,
      recipient: {
        // forward recipientType unchanged from inkindPage, required in type
        type: inkindPage.recipientType,
        fullName: pageSettingsValues.recipientFullName,
        address1: pageSettingsValues.address1,
        address2: pageSettingsValues.address2,
        city: pageSettingsValues.city,
        state: pageSettingsValues.state,
        postalCode: pageSettingsValues.postalCode,
        email: pageSettingsValues.recipientEmail,
      },
      privacySettings: {
        isPublic: pageSettingsValues.showPageEnabled,
        canFeature: pageSettingsValues.featurePageEnabled,
        allowCrawlers: pageSettingsValues.searchEnginesEnabled,
        allowPageSharing: pageSettingsValues.pageSharingEnabled,
        allowPhoneCalls: pageSettingsValues.phoneCallsEnabled,
        allowTexts: pageSettingsValues.textMessagesEnabled,
        allowVisitors: pageSettingsValues.visitorsEnabled,
        allowFlowers: pageSettingsValues.flowersEnabled,
      },
      alternateContact: {
        phoneNumber: pageSettingsValues.alternateNumber,
        fullName: pageSettingsValues.alternateNumberOwnerName,
      },
      enableSuggestedArticles: pageSettingsValues.enableSuggestedArticles,
      enableSuggestedProducts: pageSettingsValues.enableSuggestedProducts,
    };

    // no optimistic updates here because we need the modal to close only after an actual
    // round trip to the server to make sure the page doesnt flash while new information is still to come
    await patchInkind(inkindRouteId, patchInkindPageData);
    await revalidateInkindPage();
    await revalidateGoFundMeCampaignValidity();

    if (onClose) {
      onClose();
    }
  };

  // collect values via form
  // PageTitleSection,
  // RecipientInfoSection,
  //
  // collect values via onChange handlers
  // FeaturesSection,
  // PrivacySettingsSection,
  //
  // independent section with it's own form and endpoints
  // OrganizersSection,

  const handleDeletePage = async () => {
    const response = await UI.confirm(t(translationKeys.deactivatePageConfirmationText), {
      title: t(translationKeys.deactivatePage),
      okButtonProps: { variant: 'danger' },
    });

    if (!response) {
      return;
    }

    try {
      setDeletePageLoading(true);
      await removeOrUnfollowPage(userId, inkindRouteId);
      setDeletePageLoading(false);

      // close the modal, so Intercom default launcher is shown again
      if (onClose) {
        onClose();
      }

      // redirect to user profile, only on success
      navigateTo(routes.userProfile);
    } catch (error) {
      setDeletePageLoading(false);

      console.error('Failed to delete page.', inkindRouteId, error);
      UI.notifyError('There was an error deactivating your page. Please try again soon.');
    }
  };

  const scrollToFirstInvalidField = (): void => {
    if (!isBrowser()) {
      return;
    }

    const elementWithScroll = buttonsPortal ? document.querySelector('.gik-modal__content-wrapper') : null;
    const firstInvalidField = document.querySelector(
      '.gik-inkind-page-settings-form__form .gik-form-group__error'
    ) as HTMLElement;
    const offset = -43; // text field height + margin, 40 + 3

    if (!firstInvalidField) {
      return;
    }

    scrollIntoView(firstInvalidField, {
      offset,
      container: elementWithScroll as HTMLElement,
    });
  };

  // buttons for the form, skeleton has it's own definition for buttons
  const getButtons = (valid: boolean, submitting: boolean): JSX.Element => {
    const preventDelete = submitting || deletePageLoading;
    const preventIntercom = preventDelete;
    const preventSave = submitting || deletePageLoading;

    return (
      <footer className={bem('footer', [{ 'is-not-modal': !buttonsPortal }])}>
        <Button
          key="deactivate-page"
          variant="danger"
          onClick={() => !preventDelete && handleDeletePage()}
          prepend={<SvgIcon name="TrashOutline" />}
          disabled={submitting}
          loading={deletePageLoading}
        >
          {t(translationKeys.deletePage)}
        </Button>
        <Button variant="default-light" onClick={() => !preventIntercom && openIntercomWindow()} circle>
          <SvgIcon name="InterrogationRed" set="gik-icons" />
        </Button>
        <Button key="cancel" onClick={() => onClose && onClose()} variant="default">
          {t(translationKeys.cancel)}
        </Button>
        <Button
          key="save"
          variant="primary"
          type="submit"
          loading={submitting}
          disabled={!valid}
          onClick={() => !preventSave && (valid ? handleClick() : scrollToFirstInvalidField())}
        >
          {t(translationKeys.save)}
        </Button>
      </footer>
    );
  };

  const isLoading = !inkindPage;
  const isError = isLoading && inkindPageError;

  if (isLoading) {
    return <InkindPageSettingsSkeleton buttonsPortal={buttonsPortal} />;
  }
  if (isError) {
    return <InkindPageSettingsSkeleton buttonsPortal={buttonsPortal} />;
  }

  const initialFormValues = getInitialFormValues(inkindPage);

  return (
    <div className={bem()}>
      <Form
        onSubmit={handleSubmit}
        onReady={() => setIsFormReady(true)}
        initialValues={initialFormValues}
        schema={schema}
        disableScrollToFirstError
        vertical
        id={formId}
        className={bem('form')}
        render={({ valid, submitting }) => {
          const isAllValid = valid && getFeaturesSectionValid();
          const buttons = getButtons(isAllValid, submitting);

          return (
            <>
              <PageTitleSection id={pageSettingsNavItemIds.title} />
              <FeaturesSection
                id={pageSettingsNavItemIds.features}
                values={featuresSectionValues}
                onChange={setFeaturesSectionValues}
              />
              <PrivacySettingsSection
                id={pageSettingsNavItemIds.privacy}
                values={privacySettingsValues}
                onChange={setPrivacySettingsValues}
                variant="page-settings"
              />
              <RecipientInfoSection id={pageSettingsNavItemIds.recipient} />

              {ReactDOM.createPortal(buttons, buttonsPortal ? buttonsPortal() : buttonsNonModalPortal())}
            </>
          );
        }}
      />
      {isFormReady && (
        <OrganizersSection
          routeId={inkindRouteId}
          id={pageSettingsNavItemIds.organizers}
          className={bem('organizers-section')}
        />
      )}
      {!inkindPage.isPremium && <InkindPageUpgradeSection />}
      {/* when form is not loaded in a modal */}
      <div id={buttonsNonModalPortalId} />
    </div>
  );
};

export default withSuspense(InkindPageSettingsForm, InkindPageSettingsSkeleton);
